import React from 'react';
import {
  Box, Button, Card, CardContent, Chip, Divider, LinearProgress, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import AccessibleOutlinedIcon from '@mui/icons-material/AccessibleOutlined';

const palette = {
  accent: '#3FA7D6',
  accentDark: '#1565c0',
  accentSoft: '#EBF8FF',
  border: '#e0ecfa',
  borderStrong: '#c5d8f0',
  headerBg: '#F9F9F9',
  text: '#222',
  muted: '#666',
};

const addressLines = (residence) => {
  const street = [residence.loc_addr_line1, residence.loc_addr_line2].filter(Boolean).join(', ');
  const locality = [residence.loc_city, [residence.loc_state, residence.loc_zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [street, locality].filter(Boolean);
};

const SectionCard = ({ title, children }) => (
  <Card elevation={0} sx={{ border: `1px solid ${palette.border}`, borderRadius: '6px', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ px: 1.5, py: 1, backgroundColor: palette.headerBg, borderBottom: `1px solid ${palette.border}` }}>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: palette.text, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 1.5, flex: 1 }}>{children}</Box>
  </Card>
);

const ResidenceOverview = ({ residence, assignments, rooms, onNavigateToMembers, onNavigateToRooms }) => {
  const occupancy = Number(residence.current_occupancy) || 0;
  const capacity = Number(residence.max_capacity) || 0;
  const vacancy = Math.max(0, Number(residence.current_vacancy) || 0);
  const occupancyPercent = capacity ? Math.min(100, (occupancy / capacity) * 100) : 0;
  const lines = addressLines(residence);

  const stats = [
    { icon: <PeopleOutlineOutlinedIcon />, value: occupancy, label: 'Members', color: '#3e9b41' },
    { icon: <MeetingRoomOutlinedIcon />, value: rooms.length, label: 'Rooms', color: palette.accent },
    { icon: <HotelOutlinedIcon />, value: vacancy, label: 'Vacancies', color: '#c98624' },
  ];

  return (
    <Box sx={{ p: 2, backgroundColor: '#fff' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '250px minmax(360px, 1fr) 310px' }, gap: 2, alignItems: 'stretch' }}>
        <Box>
          <Card elevation={0} sx={{ borderRadius: '6px', border: `1px solid ${palette.border}`, mb: 1.5 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 3 }}>
                <HomeWorkOutlinedIcon sx={{ color: palette.accent, fontSize: 30 }} />
                <Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.15 }}>{residence.residence_name}</Typography>
                  <Chip label={vacancy > 0 ? 'Available' : capacity > 0 ? 'At Capacity' : 'No Rooms'} size="small" sx={{ mt: 0.5, height: 20, fontSize: 11, color: '#fff', backgroundColor: vacancy > 0 ? '#39a852' : '#8b928b' }} />
                </Box>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 1 }}>Residence Details</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1.25, alignItems: 'flex-start' }}>
                <LocationOnOutlinedIcon sx={{ color: palette.accent, fontSize: 18, mt: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12, color: palette.muted, fontWeight: 700 }}>Location</Typography>
                  {lines.length ? lines.map((line) => <Typography key={line} sx={{ fontSize: 13, lineHeight: 1.25 }}>{line}</Typography>) : <Typography sx={{ fontSize: 13 }}>No address assigned</Typography>}
                </Box>
              </Box>
              {!!lines.length && (
                <Button variant="outlined" size="small" startIcon={<ContentCopyOutlinedIcon />} onClick={() => navigator.clipboard?.writeText(lines.join('\n'))} sx={{ mb: 1.5, fontSize: 11, py: 0.25, borderColor: palette.borderStrong, color: palette.accentDark, fontWeight: 600 }}>
                  Copy Address
                </Button>
              )}
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Box><Typography sx={{ fontSize: 12, color: palette.muted, fontWeight: 700 }}>Floors</Typography><Typography sx={{ fontSize: 13 }}>{residence.no_of_floors || 1}</Typography></Box>
                <Box><Typography sx={{ fontSize: 12, color: palette.muted, fontWeight: 700 }}>Elevators</Typography><Typography sx={{ fontSize: 13 }}>{residence.has_elevators ? 'Available' : 'None'}</Typography></Box>
              </Box>
            </CardContent>
          </Card>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
            {stats.map((stat) => (
              <Card key={stat.label} elevation={0} sx={{ border: `1px solid ${palette.border}`, borderRadius: '5px' }}>
                <CardContent sx={{ textAlign: 'center', p: 1, '&:last-child': { pb: 1 } }}>
                  <Box sx={{ color: stat.color, display: 'flex', justifyContent: 'center', '& svg': { fontSize: 24 } }}>{stat.icon}</Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, color: stat.color, lineHeight: 1 }}>{stat.value}</Typography>
                  <Typography sx={{ fontSize: 11, color: palette.muted, lineHeight: 1.15 }}>{stat.label}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gap: 2, gridTemplateRows: { xs: 'auto', lg: 'repeat(2, minmax(0, 1fr))' }, minHeight: 0 }}>
          <SectionCard title="Current Residence Members">
            <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${palette.border}` }}>
              <Table size="small">
                <TableHead><TableRow sx={{ backgroundColor: palette.accentSoft }}>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell><TableCell sx={{ fontWeight: 700 }}>Room</TableCell><TableCell sx={{ fontWeight: 700 }}>Admitted</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {assignments.slice(0, 5).map((member) => (
                    <TableRow key={member.assignment_id} hover>
                      <TableCell>{member.cus_name}</TableCell><TableCell>{member.room_identifier || '-'}</TableCell><TableCell>{member.admission_date ? new Date(member.admission_date).toLocaleDateString() : '-'}</TableCell>
                    </TableRow>
                  ))}
                  {!assignments.length && <TableRow><TableCell colSpan={3} sx={{ color: palette.muted }}>No members assigned.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
            <Button size="small" onClick={onNavigateToMembers} sx={{ mt: 1 }}>View Members</Button>
          </SectionCard>

          <SectionCard title="Rooms">
            <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${palette.border}` }}>
              <Table size="small">
                <TableHead><TableRow sx={{ backgroundColor: palette.accentSoft }}>
                  <TableCell sx={{ fontWeight: 700 }}>Room</TableCell><TableCell sx={{ fontWeight: 700 }}>Type</TableCell><TableCell sx={{ fontWeight: 700 }}>Occupancy</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {rooms.slice(0, 5).map((room) => (
                    <TableRow key={room.room_id} hover><TableCell>{room.room_identifier}</TableCell><TableCell>{room.room_type}</TableCell><TableCell>{room.current_occupancy || 0}/{room.no_of_beds}</TableCell></TableRow>
                  ))}
                  {!rooms.length && <TableRow><TableCell colSpan={3} sx={{ color: palette.muted }}>No rooms added.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
            <Button size="small" onClick={onNavigateToRooms} sx={{ mt: 1 }}>View Rooms</Button>
          </SectionCard>
        </Box>

        <Box sx={{ display: 'grid', gap: 2, alignContent: 'start' }}>
          <SectionCard title="Occupancy Summary">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}><Typography sx={{ fontWeight: 700 }}>Occupied Beds</Typography><Typography>{occupancy} / {capacity}</Typography></Box>
            <LinearProgress variant="determinate" value={occupancyPercent} sx={{ height: 9, borderRadius: 5, mb: 1 }} />
            <Typography sx={{ fontSize: 12, color: palette.muted }}>{capacity ? `${Math.round(occupancyPercent)}% occupied` : 'Add rooms to establish capacity'}</Typography>
          </SectionCard>
          <SectionCard title="Accessibility">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AccessibleOutlinedIcon sx={{ color: palette.accent }} /><Typography>{rooms.filter((room) => room.is_accessible).length} accessible room{rooms.filter((room) => room.is_accessible).length === 1 ? '' : 's'}</Typography></Box>
            <Typography sx={{ mt: 1, fontSize: 13, color: palette.muted }}>{residence.has_elevators ? 'Elevator access is available.' : 'No elevator access recorded.'}</Typography>
          </SectionCard>
        </Box>
      </Box>
    </Box>
  );
};

export default ResidenceOverview;
